/* eslint-disable lingui/no-unlocalized-strings */
import React from 'react';

import { SkeletonLoader } from '@/components/loaders';

type SimpleSankeyDiagramSkeletonProps = {
  width?: number;
  height?: number;
  margin?: number;
};

// Define vertical padding as a ratio of height (for spacing between nodes)
const VERTICAL_PADDING = 0.04; // 4% of height between stacked nodes

// Vendor nodes (rightmost column), stacked from top to bottom, filling the height
const VENDOR_HEIGHTS = [0.18, 0.14, 0.14, 0.14, 0.08, 0.04]; // ratios of total height
const VENDOR_PADDING = VERTICAL_PADDING; // between vendor nodes
const VENDOR_TOTAL_HEIGHT =
  VENDOR_HEIGHTS.reduce((a, b) => a + b, 0) + VENDOR_PADDING * (VENDOR_HEIGHTS.length - 1);

// Channel nodes (middle column), each sized to sum of their child vendor heights (no padding)
const CHANNEL_HEIGHTS = [
  VENDOR_HEIGHTS[0], // chnl_direct → vndr_linkedin
  VENDOR_HEIGHTS[1] + VENDOR_HEIGHTS[2] + VENDOR_HEIGHTS[3], // chnl_paid_social → vndr_google_organic_social, vndr_6sense, vndr_demandbase
  VENDOR_HEIGHTS[4] + VENDOR_HEIGHTS[5], // chnl_organic_social → vndr_google_organic_social, vndr_facebook
];
const CHANNEL_PADDING = VERTICAL_PADDING; // between channel nodes
const CHANNEL_TOTAL_HEIGHT =
  CHANNEL_HEIGHTS.reduce((a, b) => a + b, 0) + CHANNEL_PADDING * (CHANNEL_HEIGHTS.length - 1);

// Root node (leftmost column), height = sum of channel heights (no padding)
const ROOT_HEIGHT = CHANNEL_HEIGHTS.reduce((a, b) => a + b, 0);

// Calculate y positions for vendor nodes (stacked)
const vendorYPositions = [0];
for (let i = 1; i < VENDOR_HEIGHTS.length; i++) {
  vendorYPositions[i] = vendorYPositions[i - 1] + VENDOR_HEIGHTS[i - 1] + VENDOR_PADDING;
}
// Calculate y positions for channel nodes (stacked)
const channelYPositions = [0];
for (let i = 1; i < CHANNEL_HEIGHTS.length; i++) {
  channelYPositions[i] = channelYPositions[i - 1] + CHANNEL_HEIGHTS[i - 1] + CHANNEL_PADDING;
}

// Calculate y position for root node (centered in SVG)
const rootY = (1 - ROOT_HEIGHT) / 2;

// Calculate y position for vendor stack (centered to fill height)
const vendorY = (1 - VENDOR_TOTAL_HEIGHT) / 2;

// Calculate y position for channel stack (centered in SVG)
const channelY = (1 - CHANNEL_TOTAL_HEIGHT) / 2;

// Node width as a ratio of width
const NODE_W = 0.12;
// X positions for columns (fill full width)
const X_ROOT = 0;
const X_CHANNEL = 0.5 - NODE_W / 2;
const X_VENDOR = 1 - NODE_W;

const NODES = [
  // Root node
  { key: 'root', x: X_ROOT, y: rootY, w: NODE_W, h: ROOT_HEIGHT },
  // Channels
  {
    key: 'chnl_direct',
    x: X_CHANNEL,
    y: channelY + channelYPositions[0],
    w: NODE_W,
    h: CHANNEL_HEIGHTS[0],
  },
  {
    key: 'chnl_paid_social',
    x: X_CHANNEL,
    y: channelY + channelYPositions[1],
    w: NODE_W,
    h: CHANNEL_HEIGHTS[1],
  },
  {
    key: 'chnl_organic_social',
    x: X_CHANNEL,
    y: channelY + channelYPositions[2],
    w: NODE_W,
    h: CHANNEL_HEIGHTS[2],
  },
  // Vendors
  {
    key: 'vndr_linkedin',
    x: X_VENDOR,
    y: vendorY + vendorYPositions[0],
    w: NODE_W,
    h: VENDOR_HEIGHTS[0],
  },
  {
    key: 'vndr_google_paid_social',
    x: X_VENDOR,
    y: vendorY + vendorYPositions[1],
    w: NODE_W,
    h: VENDOR_HEIGHTS[1],
  },
  {
    key: 'vndr_6sense',
    x: X_VENDOR,
    y: vendorY + vendorYPositions[2],
    w: NODE_W,
    h: VENDOR_HEIGHTS[2],
  },
  {
    key: 'vndr_demandbase',
    x: X_VENDOR,
    y: vendorY + vendorYPositions[3],
    w: NODE_W,
    h: VENDOR_HEIGHTS[3],
  },
  {
    key: 'vndr_google_organic_social',
    x: X_VENDOR,
    y: vendorY + vendorYPositions[4],
    w: NODE_W,
    h: VENDOR_HEIGHTS[4],
  },
  {
    key: 'vndr_facebook',
    x: X_VENDOR,
    y: vendorY + vendorYPositions[5],
    w: NODE_W,
    h: VENDOR_HEIGHTS[5],
  },
];

const PATHS = [
  // root to channels
  { from: 'root', to: 'chnl_direct' },
  { from: 'root', to: 'chnl_paid_social' },
  { from: 'root', to: 'chnl_organic_social' },
  // channels to vendors
  { from: 'chnl_direct', to: 'vndr_linkedin' },
  { from: 'chnl_paid_social', to: 'vndr_google_paid_social' },
  { from: 'chnl_paid_social', to: 'vndr_6sense' },
  { from: 'chnl_paid_social', to: 'vndr_demandbase' },
  { from: 'chnl_organic_social', to: 'vndr_google_organic_social' },
  { from: 'chnl_organic_social', to: 'vndr_facebook' },
];

const PATH_MARGIN = 4; // px, margin between path and node

const getNodeRect = (node: (typeof NODES)[number], width: number, height: number) => ({
  x: node.x * width,
  y: node.y * height,
  w: node.w * width,
  h: node.h * height,
});

const SimpleSankeyDiagramSkeleton: React.FC<SimpleSankeyDiagramSkeletonProps> = ({
  width = 800,
  height = 400,
  margin = 25,
}) => {
  // Map node keys to their rects
  const nodeRects: Record<string, { x: number; y: number; w: number; h: number }> = {};
  NODES.forEach((node) => {
    nodeRects[node.key] = getNodeRect(node, width, height);
  });

  // For each source node, stack outgoing paths vertically (no gaps)
  const outgoingOffsets: Record<string, number> = {};
  NODES.forEach((node) => {
    outgoingOffsets[node.key] = 0;
  });
  // For each target node, only one incoming path, so offset is always 0

  // Precompute outgoing path heights for each source node
  const outgoingPathHeights: Record<string, number[]> = {};
  NODES.forEach((node) => {
    const outgoing = PATHS.filter((p) => p.from === node.key);
    if (outgoing.length === 0) return;
    const totalTargetHeight = outgoing.reduce((sum, p) => sum + nodeRects[p.to].h, 0);
    outgoingPathHeights[node.key] = outgoing.map(
      (p) => (nodeRects[p.to].h * nodeRects[node.key].h) / totalTargetHeight,
    );
  });

  // Draw paths
  const pathIndexBySource: Record<string, number> = {};
  NODES.forEach((node) => {
    pathIndexBySource[node.key] = 0;
  });
  const linkPaths = PATHS.map((path, i) => {
    const from = nodeRects[path.from];
    const to = nodeRects[path.to];
    // Outgoing band height for this path
    let bandHeight = to.h;
    let fromOffset = 0;
    if (outgoingPathHeights[path.from]) {
      bandHeight = outgoingPathHeights[path.from][pathIndexBySource[path.from]];
      fromOffset = outgoingOffsets[path.from];
      outgoingOffsets[path.from] += bandHeight;
      pathIndexBySource[path.from]++;
    }
    // Always fill the target node
    const toOffset = 0;
    // Path coordinates
    const fromTopRight = { x: from.x + from.w + PATH_MARGIN, y: from.y + fromOffset };
    const fromBottomRight = {
      x: from.x + from.w + PATH_MARGIN,
      y: from.y + fromOffset + bandHeight,
    };
    const toTopLeft = { x: to.x - PATH_MARGIN, y: to.y + toOffset };
    const toBottomLeft = { x: to.x - PATH_MARGIN, y: to.y + toOffset + to.h };
    // Bezier control points
    const dx = toTopLeft.x - fromTopRight.x;
    const c1x = fromTopRight.x + dx * 0.5;
    const c2x = toTopLeft.x - dx * 0.5;
    const topPath = `C${c1x},${fromTopRight.y} ${c2x},${toTopLeft.y} ${toTopLeft.x},${toTopLeft.y}`;
    const bottomPath = `C${c2x},${toBottomLeft.y} ${c1x},${fromBottomRight.y} ${fromBottomRight.x},${fromBottomRight.y}`;
    const d = [
      `M${fromTopRight.x},${fromTopRight.y}`,
      topPath,
      `L${toBottomLeft.x},${toBottomLeft.y}`,
      bottomPath,
      'Z',
    ].join(' ');
    return <path key={i} d={d} fill="#E0E0E0" opacity={0.7} stroke="none" />;
  });

  // Draw nodes
  const nodeRectsSvg = NODES.map((node) => {
    const rect = nodeRects[node.key];
    return <rect key={node.key} x={rect.x} y={rect.y} width={rect.w} height={rect.h} />;
  });

  return (
    <SkeletonLoader
      width={width}
      height={height}
      viewBox={`-${margin} -${margin} ${width + 2 * margin} ${height + 2 * margin}`}
    >
      {linkPaths}
      {nodeRectsSvg}
    </SkeletonLoader>
  );
};

export default SimpleSankeyDiagramSkeleton;
